import { useState } from "react";
import { Check, Copy, ExternalLink, BadgeCheck, Sparkles } from "lucide-react";

const CODE = "RETA20";
const PRODUCT_URL = "https://bioinfinity.co/retatrutide/glp3-r";

export default function DiscountCTA({ variant = "full" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CODE);
    } catch {
      const el = document.createElement("textarea");
      el.value = CODE;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (variant === "inline") {
    return (
      <div
        data-testid="discount-cta-inline"
        className="bento-card flex flex-wrap items-center gap-3 p-4"
        style={{ background: "linear-gradient(135deg, #eef6ff, #dbeafe)", borderColor: "#93c5fd" }}
      >
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "rgba(17,60,110,0.1)" }}>
          <Sparkles className="h-3.5 w-3.5" style={{ color: "#113C6E" }} />
        </div>
        <span className="text-sm text-slate-700">
          Need GLP3-R for this protocol?{" "}
          <span className="font-bold text-slate-900">20% off</span> BioInfinity Lab —
        </span>
        <button
          data-testid="inline-copy-code-btn"
          onClick={handleCopy}
          className="btn-lift inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 rounded-lg font-mono text-sm font-bold tracking-wider hover:border-blue-400 transition-colors"
          style={{ color: "#113C6E" }}
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied!" : CODE}
        </button>
        <a
          href={PRODUCT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="inline-product-link"
          className="inline-flex items-center gap-1 text-sm font-bold hover:opacity-80 transition-opacity"
          style={{ color: "#113C6E" }}
        >
          Shop Now
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    );
  }

  return (
    <div
      data-testid="discount-cta-full"
      className="bento-card overflow-hidden"
      style={{ background: "linear-gradient(160deg, #ffffff 60%, #eef6ff 100%)" }}
    >
      <div className="p-8 md:p-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-sm" style={{ background: "#113C6E" }}>
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-slate-900">Featured Lab</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full text-[11px] font-bold text-emerald-700">
                <BadgeCheck className="h-3 w-3" /> Verified
              </span>
            </div>
            <span className="text-xs text-slate-500">BioInfinity Lab — Official Partner</span>
          </div>
        </div>

        <h3 data-testid="discount-cta-headline" className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mb-3">
          Exclusive Researcher Access
        </h3>

        <p className="text-base leading-relaxed text-slate-500 mb-8 max-w-lg">
          Get <span className="font-bold text-slate-900">20% off</span>{" "}
          BioInfinity Lab GLP3-R for your laboratory studies. Third-party tested,
          &ge;99% purity — the gold standard for Retatrutide research.
        </p>

        <div className="mb-6">
          <label className="block text-xs tracking-wider uppercase font-semibold text-slate-400 mb-2">
            Your Discount Code
          </label>
          <div className="flex items-stretch max-w-sm">
            <div className="flex-1 flex items-center px-5 py-3.5 bg-slate-50 border border-slate-200 border-r-0 rounded-l-2xl font-mono text-lg font-bold text-slate-900 tracking-widest select-all">
              {CODE}
            </div>
            <button
              data-testid="full-copy-code-btn"
              onClick={handleCopy}
              className="btn-lift flex items-center gap-2 px-6 py-3.5 text-white text-sm font-bold rounded-r-2xl transition-colors"
              style={{ background: "#113C6E" }}
            >
              {copied ? (
                <><Check className="h-4 w-4" /> Copied!</>
              ) : (
                <><Copy className="h-4 w-4" /> Copy Code</>
              )}
            </button>
          </div>
        </div>

        <a
          href={PRODUCT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="full-product-link"
          className="btn-lift inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-bold rounded-2xl transition-colors"
          style={{ background: "#113C6E" }}
        >
          Visit BioInfinity Lab
          <ExternalLink className="h-4 w-4" />
        </a>

        <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap gap-5">
          {["Third-Party COA Verified", "≥99% Purity", "GMP Compliant", "US Shipping"].map((item) => (
            <span key={item} className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
              <Check className="h-3 w-3 text-emerald-500" />
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
